import { isEmpty } from "ramda";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Autocomplete, Button, Icon, Loading, Modal, Spinner, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import {
  useLazyFindVehicleQuery,
  useStartVeivoValidationFromVinMutation,
  VeivoFindResult,
} from "@/redux/apis/vehicle/veivoApi";
import { addToast, getCountryName } from "@/utils";
import { getCountryFlagSrc } from "@/utils/getCountryFlagSrc";
import { sanitiseRego } from "../AddVehicle/modals/AddVehicleVinModal";
import { VehicleCard, vehicleTitle } from "./VehicleCard";

type StateOption = { name: string; value: string };

/** Same rule as the plate flow's input. */
const REGO_PATTERN = "[A-Za-z0-9\\-]+";
const REGO_MAX_LENGTH = 16;

/** What Find answered: the car, or why it can't be added — or, before our
 *  own checks are even reached, that the registry has no car for the plate
 *  (check the number) or could not be asked (try again). */
type FindOutcome =
  | { kind: "found"; result: VeivoFindResult }
  | { kind: "registered"; plate: string; state: string }
  | { kind: "inFleet"; vehicleUuid?: string }
  | { kind: "limit"; message: string }
  | { kind: "notInRegistry"; plate: string; state: string; message: string }
  | { kind: "unavailable"; message: string };

interface FindVehicleModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Add a vehicle on the Veivo path (DEV-1902): the number plate and state first.
 *
 * Find runs before anything is uploaded or paid for — the plan's room, what
 * the registry says the car is, then whether the plate is already somebody's
 * vehicle, and the documents its country needs — so an operator who cannot add
 * this car learns it in ten seconds, not after a walk-around. The registry must
 * name the car: a plate it has no vehicle for stops here, as does a registry
 * that could not be asked. "Yes — verify this vehicle" starts the verification
 * and opens the five-step page.
 */
export const FindVehicleModal = ({ open, onClose }: FindVehicleModalProps) => {
  const navigate = useNavigate();
  const { id: operatorId, country } = useOperator();
  const [rego, setRego] = useState("");
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState<FindOutcome | null>(null);

  const { isFetching: loadingStates, data: statesResponse } = useGetCountryStatesQuery(country);
  const [findVehicle, { isFetching: finding }] = useLazyFindVehicleQuery();
  const [startValidation, { isLoading: starting }] = useStartVeivoValidationFromVinMutation();

  const options = useMemo(
    () => statesResponse?.data.states.map((state) => ({ name: state.name, value: state.id })) ?? [],
    [statesResponse?.data.states]
  );
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const plate = sanitiseRego(rego);
  const canFind = plate.length > 0 && Boolean(selectedState);

  const reset = () => {
    setRego("");
    setSelectedState(null);
    setQuery("");
    setOutcome(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFind = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canFind || !selectedState) return;
    findVehicle({ registrationNumber: plate, stateUuid: selectedState.value })
      .unwrap()
      .then((result) => setOutcome({ kind: "found", result }))
      .catch((error: { status?: number; data?: { code?: string; vehicleUuid?: string; message?: string } }) => {
        const code = error?.data?.code;
        if (code === "VEHICLE_ALREADY_REGISTERED") {
          return setOutcome({ kind: "registered", plate, state: selectedState.name });
        }
        if (code === "VEHICLE_ALREADY_IN_FLEET") return setOutcome({ kind: "inFleet", vehicleUuid: error.data?.vehicleUuid });
        if (code === "VEHICLE_LIMIT_REACHED") {
          return setOutcome({ kind: "limit", message: error.data?.message ?? "Your plan's vehicle limit has been reached." });
        }
        if (code === "VEHICLE_NOT_IN_REGISTRY") {
          return setOutcome({
            kind: "notInRegistry",
            plate,
            state: selectedState.name,
            message: error.data?.message ?? "Check the number plate and the state, then try again.",
          });
        }
        if (code === "REGISTRY_UNAVAILABLE") {
          return setOutcome({
            kind: "unavailable",
            message: error.data?.message ?? "We couldn't check this number plate right now. Try again in a moment.",
          });
        }
        getErrorMessages(error as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
      });
  };

  const handleVerify = (result: VeivoFindResult) => {
    startValidation({
      registration_number: result.registrationNumber,
      state_uuid: result.state.uuid,
      // The vehicle is created at Review, where the fee is shown (DEV-1898).
      create_on: "confirm",
      ...(result.vehicle ? { vehicle_summary: result.vehicle } : {}),
    })
      .unwrap()
      .then(({ validationId }) => {
        reset();
        onClose();
        navigate(`/operator/${operatorId}/vehicles/my-vehicles/verify/${validationId}`);
      })
      .catch((error) => getErrorMessages(error).forEach((m) => addToast("danger", m)));
  };

  if (outcome?.kind === "found") {
    return (
      <FoundView
        open={open}
        result={outcome.result}
        starting={starting}
        onVerify={() => handleVerify(outcome.result)}
        onBack={() => setOutcome(null)}
        onClose={handleClose}
      />
    );
  }

  if (outcome?.kind === "registered") {
    return (
      <Modal open={open} onClose={handleClose} className="w-[85vw] !p-6 md:w-[480px] lg:w-[480px]">
        <div className="flex flex-col gap-4">
          <Typography variant="title">This vehicle is already registered</Typography>
          <VehicleCard title={outcome.plate} subtitle={outcome.state} />
          <Alert
            type="danger"
            message={
              <span>
                <span className="block font-semibold">Already on another account</span>
                {outcome.plate} ({outcome.state}) is on another operator&apos;s account, so it can&apos;t be added again. Nothing has been
                uploaded yet — contact support if you think this is a mistake.
              </span>
            }
          />
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => window.open("mailto:support@rideminder.com", "_blank", "noreferrer")}
            >
              Contact support
            </Button>
            <Button variant="secondary" size="lg" className="w-full" onClick={reset}>
              Try a different plate
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} className="w-[85vw] !p-6 md:w-[480px] lg:w-[480px]">
      <div className="relative flex flex-col">
        {finding && <Loading />}
        <Typography variant="title">Add a vehicle</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          Start with the number plate. We&apos;ll find the vehicle and check it isn&apos;t already registered.
        </Typography>

        <form className="mt-5 flex flex-col" onSubmit={handleFind}>
          <label htmlFor="find-rego" className="flex flex-col gap-1">
            <Typography variant="paragraph">Number plate</Typography>
            <TextInput
              id="find-rego"
              title="Only letters, numbers and hyphens are allowed"
              pattern={REGO_PATTERN}
              placeholder="Enter the number plate"
              value={rego}
              onChange={(e) => {
                setOutcome(null);
                setRego(sanitiseRego(e.target.value));
              }}
              required
              autoFocus
              maxLength={REGO_MAX_LENGTH}
              className="uppercase"
            />
          </label>

          <label htmlFor="find-state" className="mt-5 flex flex-col gap-1">
            <Typography variant="paragraph">State or territory</Typography>
            <Autocomplete<StateOption>
              className="w-full"
              placeholder="Search state"
              options={filteredOptions}
              value={selectedState}
              onValueChange={(value) => {
                setOutcome(null);
                setSelectedState(value);
              }}
              onQueryChange={setQuery}
              isLoading={loadingStates}
              disabled={isEmpty(options) || loadingStates}
            />
          </label>

          {outcome?.kind === "inFleet" && (
            <Alert
              type="warning"
              className="mt-5"
              message={
                <span>
                  This vehicle is already in your fleet.{" "}
                  {outcome.vehicleUuid && (
                    <Link
                      to={`/operator/${operatorId}/vehicles/my-vehicles/${outcome.vehicleUuid}`}
                      className="font-semibold underline"
                      onClick={handleClose}
                    >
                      Open it
                    </Link>
                  )}
                </span>
              }
            />
          )}

          {outcome?.kind === "limit" && (
            <Alert
              type="warning"
              className="mt-5"
              message={
                <span>
                  {outcome.message}{" "}
                  <Link
                    to={`/operator/${operatorId}/payments/subscriptions/subscription-plan`}
                    className="font-semibold underline"
                    onClick={handleClose}
                  >
                    See plans
                  </Link>
                </span>
              }
            />
          )}

          {outcome?.kind === "notInRegistry" && (
            <Alert
              type="danger"
              className="mt-5"
              message={
                <span>
                  <span className="block font-semibold">
                    No vehicle found for {outcome.plate} in {outcome.state}
                  </span>
                  {outcome.message}
                </span>
              }
            />
          )}

          {outcome?.kind === "unavailable" && <Alert type="warning" className="mt-5" message={outcome.message} />}

          <div className="mt-6 flex flex-col gap-3">
            <Button type="submit" variant="primary" size="lg" className="w-full" disabled={finding || !canFind}>
              {finding ? <Spinner /> : "Find vehicle"}
            </Button>
            <Button type="button" variant="secondary" size="lg" className="w-full" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

/** "Is this the vehicle?" — the registry's car. (The plate-only wording is
 *  kept for a result without a vehicle, which Find no longer returns: it
 *  refuses first.) */
const FoundView = ({
  open,
  result,
  starting,
  onVerify,
  onBack,
  onClose,
}: {
  open: boolean;
  result: VeivoFindResult;
  starting: boolean;
  onVerify: () => void;
  onBack: () => void;
  onClose: () => void;
}) => {
  const { vehicle, state, registrationNumber } = result;
  const flagSrc = getCountryFlagSrc(result.country);
  const details = [registrationNumber, state.abbreviation, vehicle?.year, vehicle?.colour].filter(Boolean).join(" · ");

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] !p-6 md:w-[480px] lg:w-[480px]">
      <div className="flex flex-col gap-4">
        <div>
          <Typography variant="title">{vehicle ? "Is this the vehicle?" : "Verify this vehicle"}</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            {vehicle
              ? "Found from the number plate. Next, we'll verify it with its documents and a quick walk-around."
              : "We couldn't look this plate up — that's fine: its documents and a quick walk-around will tell us the vehicle."}
          </Typography>
        </div>

        <VehicleCard title={vehicle ? vehicleTitle(vehicle) : registrationNumber} subtitle={vehicle ? details : state.name} />

        <div className="flex items-center gap-2 rounded-lg bg-success-light px-4 py-2.5 text-success-dark">
          <Icon name="TickCircle" variant="Bold" size="sm" />
          <Typography variant="action">Not registered with another operator</Typography>
        </div>

        <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
          <div className="flex items-center justify-between bg-neutral-surface-gray px-4 py-3">
            <Typography variant="action">What you&apos;ll need</Typography>
            <span className="inline-flex items-center gap-1.5">
              {flagSrc && <img src={flagSrc} className="h-4 w-4 rounded-full object-cover" alt="" />}
              <Typography variant="small">{getCountryName(result.country) ?? result.country} requirements</Typography>
            </span>
          </div>
          <ul className="divide-y divide-neutral-mid-gray">
            {result.requiredDocuments.map((doc) => (
              <NeedRow
                key={doc.docType}
                icon="DocumentText"
                title={doc.name}
                description={doc.description}
                hint={doc.optional ? "Optional" : "1 min"}
              />
            ))}
            <NeedRow
              icon="Car"
              title="Walk-around + VIN photo"
              description="Take your phone to the car and walk around it once"
              hint="2 min"
            />
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          <Button variant="primary" size="lg" className="w-full" disabled={starting} onClick={onVerify}>
            {starting ? <Spinner /> : vehicle ? "Yes — verify this vehicle" : "Verify this vehicle"}
          </Button>
          <Button variant="secondary" size="lg" className="w-full" onClick={onBack}>
            {vehicle ? "Not this vehicle" : "Back"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const NeedRow = ({ icon, title, description, hint }: { icon: string; title: string; description: string; hint: string }) => (
  <li className="flex items-center gap-3 px-4 py-3">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
      <Icon name={icon} size="md" />
    </span>
    <div className="min-w-0 flex-1">
      <Typography variant="action">{title}</Typography>
      <Typography variant="small-regular" className="text-neutral-dark-gray">
        {description}
      </Typography>
    </div>
    <Typography variant="small-regular" className="shrink-0 text-neutral-dark-gray">
      {hint}
    </Typography>
  </li>
);
